import { useMutation, useQuery } from '@tanstack/react-query'
import axios from 'axios'
import type {
  Addon,
  BaseParamsDto,
  CalcAmountRealtyFlatRequest,
  CalcAmountResponse,
  CalcMinAmountHouseholdRequest,
  CalcMinAmountRealtyRequest,
  ConfirmContractRequest,
  ConfirmContractResponse,
  ContractEmailLog,
  CreateContractAdditionallyRequest,
  CreateContractAdditionallyResponse,
  CreateContractRequest,
  CreateContractResponse,
  FormWarning,
  GetWarningRequest,
  InsuranceParamsDto,
  InsuranceParamsPropertyDto,
  InsuranceParamsTravelDto,
  IsContractConfirmedResponse,
  LastWSProps,
  Property,
  SaveStep2Request,
  SaveStep2TravelRequest,
  SaveVehicleAdditionalInfoRequest,
  SimpleItem,
  Step1CategoryWarning,
  Step1PropertyWarning,
  Step1Warning,
  Step2Property,
  WizardPropertyAdditionalData,
  WizardTravelAdditionalData,
  WizardVehicleAdditionalData,
} from 'BrokerTsLib/src/dtos'
import type { ContractDocType } from 'BrokerTsLib/src/enums'
import { Product } from 'BrokerTsLib/src/enums'
import type { GenericQueryOptions } from 'BrokerTsLib/src/hooks'

import apiClient from 'BrokerTsLib/src/Utils/apiClient'
import { baseUrl } from '.'
import type { OkAppsMeetingRecord } from '../dtos/OkAppsMeetingRecord'
import { useApp } from './useApp'

export type UpdateAddonsRequest = {
  paymentFrequency?: number
  addons?: Addon[]
}

const wizardApiUrl = import.meta.env.VITE_WIZARD_VEHICLE_API
const wizardPropertyApiUrl = import.meta.env.VITE_WIZARD_PROPERTY_API
const wizardLiabilityApiUrl = import.meta.env.VITE_WIZARD_LIABILITY_API
const wizardTravelApiUrl = import.meta.env.VITE_WIZARD_TRAVEL_API

export const useUrl = () => {
  const { product } = useApp()

  let url: string | undefined = ''

  switch (product) {
    case Product.TravelInsurance:
      url = wizardTravelApiUrl
      break
    case Product.PropertyInsurance:
      url = wizardPropertyApiUrl
      break
    case Product.LiabilityInsurance:
      url = wizardLiabilityApiUrl
      break
    default:
      url = wizardApiUrl
      break
  }

  return `${baseUrl}/${url}`
}

export const useGetInsuranceParams = (
  paramsId: string,
  options?: GenericQueryOptions<
    InsuranceParamsDto | InsuranceParamsPropertyDto | InsuranceParamsTravelDto
  >,
) => {
  const url = useUrl()

  return useQuery({
    queryKey: ['getInsuranceParams', paramsId],
    queryFn: () =>
      axios.get<InsuranceParamsDto | InsuranceParamsPropertyDto | InsuranceParamsTravelDto>(
        `${url}/GetInsuranceParams?paramsId=${paramsId}`,
      ),
    ...options,
  })
}

export const useSaveInsuranceParams = () => {
  const url = useUrl()

  return useMutation({
    mutationFn: (insuranceParams: InsuranceParamsDto | InsuranceParamsPropertyDto | InsuranceParamsTravelDto) =>
      axios.post(`${url}/SaveInsuranceParams`, insuranceParams),
  })
}

export const useGetStep4 = (
  paramsId: string,
  options?: GenericQueryOptions<
    WizardVehicleAdditionalData | WizardPropertyAdditionalData | WizardTravelAdditionalData
  >,
) => {
  const url = useUrl()
  const { product } = useApp()

  return useQuery({
    queryKey: ['getStep4', paramsId, product],
    queryFn: () =>
      axios.get<
        WizardVehicleAdditionalData | WizardPropertyAdditionalData | WizardTravelAdditionalData
      >(`${url}/GetStep4?paramsId=${paramsId}`),
    ...options,
  })
}

export const useSaveStep4 = () => {
  const url = useUrl()
  return useMutation({
    mutationFn: (
      step4:
        | WizardVehicleAdditionalData
        | WizardPropertyAdditionalData
        | WizardTravelAdditionalData,
    ) => axios.post(`${url}/SaveStep4`, step4)
  })
}

export const useGetMeetingRecord = (
  paramsId: string,
  options?: GenericQueryOptions<OkAppsMeetingRecord>,
) => {
  const url = useUrl()
  const { product } = useApp()

  return useQuery({
    queryKey: ['getMeetingRecord', paramsId, product],
    queryFn: () => axios.get<OkAppsMeetingRecord>(`${url}/GetMeetingRecord?paramsId=${paramsId}`),
    ...options,
  })
}

export const useSaveMeetingRecord = () => {
  const url = useUrl()

  return useMutation({
    mutationFn: (meetingRecord: OkAppsMeetingRecord) =>
      axios.post(`${url}/SaveMeetingRecord`, meetingRecord),
  })
}

export const useCreateContract = () => {
  const url = useUrl()
  return useMutation({
    mutationFn: (request: CreateContractRequest) =>
      axios.post<CreateContractResponse>(`${url}/CreateContract`, request),
  })
}

export const useCreateContractAdditionally = () => {
  const url = useUrl()
  return useMutation({
    mutationFn: (request: CreateContractAdditionallyRequest) =>
      axios.post<CreateContractAdditionallyResponse>(`${url}/CreateContractAdditionally`, request),
  })
}

export const useCancelContract = () => {
  const url = useUrl()

  return useMutation({
    mutationFn: (insuranceId: number) =>
      apiClient.post(`${url}/CancelContract`, { insuranceId }).then(response => response.data)
  })
}

export const useReactivateContract = () => {
  const url = useUrl()

  return useMutation({
    mutationFn: (insuranceId: number) =>
      apiClient.post<{ success?: boolean }>(`${url}/ReactivateContract`, { insuranceId }).then(response => response.data),
  })
}

export const useUpdateLastWizardStep = () => {
  const url = useUrl()
  return useMutation({
    mutationFn: (data: LastWSProps) => axios.post(`${url}/UpdateLastWizardStep`, data)
  })
}

export const useGetAddons = (paramsId: string, options?: GenericQueryOptions<Addon[]>) => {
  const url = useUrl()
  const { product } = useApp()

  return useQuery({
    queryKey: ['useGetAddons', paramsId, product],
    queryFn: () => axios.get<Addon[]>(`${url}/GetAddons?paramsId=${paramsId}`),
    ...options,
  })
}

export const useUpdateAddons = (paramsId: string) => {
  const url = useUrl()
  return useMutation({
    mutationFn: (data: UpdateAddonsRequest) =>
      axios.post(`${url}/UpdateAddons?paramsId=${paramsId}`, data),
  })
}

export const useSaveStep2 = () => {
  const url = useUrl()
  return useMutation({
    mutationFn: (data: SaveStep2Request | Step2Property | SaveStep2TravelRequest) =>
      axios.post(`${url}/SaveStep2`, data),
  })
}

export const useAddVariant = (paramsId: string) => {
  const url = useUrl()
  return useMutation({
    mutationFn: (data: BaseParamsDto | Property) =>
      axios.post(`${url}/AddVariant?paramsId=${paramsId}`, data),
  })
}

export const useGetStep1Warnings = () => {
  const url = useUrl()
  return useMutation({
    mutationFn: (data: GetWarningRequest) =>
      axios.post<Step1Warning[]>(`${url}/GetWarnings`, data),
  })
}

export const useGetStep1PropertyWarnings = (
  options?: GenericQueryOptions<Step1PropertyWarning[]>,
) => {
  const url = useUrl()
  const { product } = useApp()

  return useQuery({
    queryKey: ['GetWarnings', product],
    queryFn: () => axios.get<Step1PropertyWarning[]>(`${url}/GetWarnings`),
    ...options,
  })
}

export const useGetStep1CategoryWarnings = () => {
  const url = useUrl()
  return useMutation({
    mutationFn: (data: GetWarningRequest) =>
      axios.post<Step1CategoryWarning[]>(`${url}/GetCategoryWarnings`, data),
  })
}

export const useUpdateInsuranceStartDate = (paramsId: string) => {
  const url = useUrl()
  return useMutation({
    mutationFn: (date: Date) =>
      axios.post(`${url}/UpdateInsuranceStartDate`, { insuranceStartDate: date, paramsId }),
  })
}

export const useGetVehicleInspectionDistricts = (
  offerId: number,
  options?: { enabled?: boolean }
) => {
  const url = useUrl()
  const { product } = useApp()

  return useQuery({
    queryKey: ['useGetVehicleInspectionDistricts', offerId, product],
    queryFn: () => axios.get<SimpleItem[]>(`${url}/GetVehicleInspectionDistricts?offerId=${offerId}`).then(res => res.data),
    ...options,
  })
}

export const useGetVehiclePhotoDocumentationCodes = (
  offerId: number,
  options?: { enabled?: boolean },
) => {
  const url = useUrl()
  const { product } = useApp()

  return useQuery({
    queryKey: ['useGetVehiclePhotoDocumentationCodes', offerId, product],
    queryFn: () => axios.get<SimpleItem[]>(`${url}/GetVehiclePhotoDocumentationCodes?offerId=${offerId}`).then(res => res.data),
    ...options,
  })
}

// jen pro majetek
export const useCalcRecommendedAmountHousehold = () => {
  const url = useUrl()
  return useMutation({
    mutationFn: (data: CalcMinAmountHouseholdRequest) =>
      axios.post<CalcAmountResponse>(`${url}/CalcRecommendedAmountHousehold`, data),
  })
}

// jen pro majetek
export const useCalcRecommendedAmountRealty = () => {
  const url = useUrl()
  return useMutation({
    mutationFn: (data: CalcMinAmountRealtyRequest) =>
      axios.post<CalcAmountResponse>(`${url}/CalcRecommendedAmountRealty`, data),
  })
}

// jen pro majetek a stavbu - byt
export const useCalcRecommendedFlatAmountRealty = () => {
  const url = useUrl()
  return useMutation({
    mutationFn: (data: CalcAmountRealtyFlatRequest) =>
      axios.post<CalcAmountResponse>(`${url}/CalcRecommendedAmountRealtyFlat`, data),
  })
}


export const useGetDocumentTypes = (offerId: number) => {
  const url = useUrl()
  const { product } = useApp()

  return useQuery({
    queryKey: ['documentTypes', offerId, product],
    queryFn: () =>
      axios.get<{ type: ContractDocType; description: string }[]>(`${url}/GetDocumentTypes`, {
        params: { offerId },
      }),
  })
}

export const useUploadInsuranceOfferDocument = () => {
  const url = useUrl()

  return useMutation({
    mutationFn: (x: { data: FormData; offerId: number; documentType: ContractDocType; overwrite: boolean }) =>
      axios.post(`${url}/UploadInsuranceOfferDocument`, x.data, {
        params: {
          offerId: x.offerId,
          documentType: x.documentType,
          overwrite: x.overwrite,
        },
      }),
  })
}

export const useGetContractEmailLogs = (
  paramsId: string,
  insurerOfferId?: number,
  options?: GenericQueryOptions<ContractEmailLog[]>,
) => {
  const url = useUrl()
  const { product } = useApp()

  return useQuery({
    queryKey: ['useGetContractEmailLogs', product, paramsId, insurerOfferId],
    queryFn: () =>
      axios.get<ContractEmailLog[]>(`${url}/GetContractEmailLogs`, {
        params: { paramsId, insurerOfferId }
      }),
    ...options
  })
}

export const useConfirmContract = () => {
  const url = useUrl()
  return useMutation({
    mutationFn: (data: ConfirmContractRequest) =>
      axios.post<ConfirmContractResponse>(`${url}/ConfirmContract`, data),
  })
}

export const useGetIsContractConfirmed = (
  paramsId: string,
  options?: GenericQueryOptions<IsContractConfirmedResponse>,
) => {
  const url = useUrl()
  const { product } = useApp()

  return useQuery({
    queryKey: ['useGetIsContractConfirmed', paramsId, product],
    queryFn: () => axios.get<IsContractConfirmedResponse>(`${url}/IsContractConfirmed?paramsId=${paramsId}`),
    ...options,
  })
}

export const useGetWarningsTravel = () => {
  const url = useUrl()
  const { product } = useApp()

  return useQuery({
    queryKey: ['warnings', product],
    queryFn: () => axios.get<FormWarning[]>(`${url}/GetWarnings`).then((res) => res.data)
  })
}

export const useSaveVehicleAdditionalInfo = () => {
  const url = useUrl()
  return useMutation({
    mutationFn: (request: SaveVehicleAdditionalInfoRequest) =>
      axios.post(`${url}/SaveVehicleAdditionalInfo`, request)
  })
}